import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";

import { RoleInDB, TokenWithUserAndKey } from "src/clients/taktile-api";
import { analytics } from "src/instrumentation/analytics";
import { loginWithRefreshToken, loginWithWorkspaceScope } from "src/store/api";
import { decodeTokenScope } from "src/utils/decodeTokenScope";

export type LocalStorageValue = string | undefined | null;

export const useAuthStore = create<
  ZustandStore<
    {
      refreshed: boolean;
      signed_in_user_id: string | undefined;
      signed_in_username: string | undefined;
      signed_in_avatar_url: string | undefined;
      signed_in_email: string | undefined;
      id_token: LocalStorageValue;
      refresh_token: LocalStorageValue;
      api_key: string | undefined;
      signed_in_full_name: string | undefined;
      hashed_email: string | undefined;
      roles: RoleInDB[] | undefined;
      scope_org_id: string | undefined;
      scope_ws_id: string | undefined;
    },
    {
      resetAll: () => void;
      setup: () => void;
      refresh: () => Promise<TokenWithUserAndKey | void>;
      scopeToWorkspace: (
        orgId: string,
        wsId: string,
        options?: { signal?: AbortSignal },
      ) => Promise<void>;
      setCredentials: (
        signed_in_user_id: string,
        signed_in_username: string,
        signed_in_full_name: string | undefined,
        signed_in_email: string,
        signed_in_avatar_url: string | undefined,
        id_token: string,
        refresh_token: string,
        api_key: string,
        hashed_email?: string,
        roles?: RoleInDB[] | undefined,
      ) => void;
    }
  >
>((set, get) => ({
  refreshed: false,
  signed_in_username: undefined,
  signed_in_user_id: undefined,
  signed_in_avatar_url: undefined,
  signed_in_full_name: undefined,
  api_key: undefined,
  signed_in_email: undefined,

  id_token: localStorage.getItem("id_token"),
  refresh_token: localStorage.getItem("refresh_token"),
  hashed_email: undefined,
  roles: undefined,
  scope_org_id: decodeTokenScope(localStorage.getItem("refresh_token")).orgId,
  scope_ws_id: decodeTokenScope(localStorage.getItem("refresh_token")).wsId,

  actions: {
    setup: async () => {
      set({ refreshed: false });
      await get().actions.refresh();
      set({ refreshed: true });
    },
    scopeToWorkspace: async (orgId, wsId, options) => {
      const { user, id_token, refresh_token, api_key } =
        await loginWithWorkspaceScope(orgId, wsId, options);
      // Don't apply a mint superseded by a newer workspace switch (request
      // resolved just as its query was cancelled). Bail silently rather than
      // throwing, so no error state / boundary is hit.
      if (options?.signal?.aborted) {
        return;
      }
      get().actions.setCredentials(
        user.id,
        user.username ?? "",
        user.full_name ?? undefined,
        user.email ?? "",
        user.avatar_url ?? undefined,
        id_token,
        refresh_token,
        api_key.api_key,
        user.hashed_email,
        user.roles,
      );
    },
    refresh: async () => {
      if (get().refresh_token) {
        const response = await loginWithRefreshToken();
        get().actions.setCredentials(
          response.user.id,
          response.user.username ?? "",
          response.user.full_name ?? undefined,
          response.user.email ?? "",
          response.user.avatar_url ?? undefined,
          response.id_token,
          response.refresh_token,
          response.api_key.api_key,
          response.user.hashed_email,
          response.user.roles,
        );
        return response;
      }
    },

    setCredentials: (
      signed_in_user_id: string,
      signed_in_username: string,
      signed_in_full_name: string | undefined,
      signed_in_email: string,
      signed_in_avatar_url: string | undefined,
      id_token: string,
      refresh_token: string,
      api_key: string,
      hashed_email?: string,
      roles?: RoleInDB[] | undefined,
    ) => {
      const scope = decodeTokenScope(refresh_token);
      set({
        id_token,
        refresh_token,
        signed_in_user_id,
        signed_in_username,
        signed_in_full_name,
        signed_in_email,
        signed_in_avatar_url,
        api_key,
        hashed_email,
        roles,
        scope_org_id: scope.orgId,
        scope_ws_id: scope.wsId,
      });
      localStorage.setItem("id_token", id_token);
      localStorage.setItem("refresh_token", refresh_token);

      analytics.identify(signed_in_user_id, {
        username: signed_in_username,
        $set_once: { username: signed_in_username },
      });
    },

    resetAll: () => {
      localStorage.removeItem("id_token");
      localStorage.removeItem("refresh_token");

      set({
        refreshed: undefined,
        signed_in_user_id: undefined,
        signed_in_username: undefined,
        signed_in_avatar_url: undefined,
        id_token: undefined,
        refresh_token: undefined,
        api_key: undefined,
        signed_in_full_name: undefined,
        hashed_email: undefined,
        roles: undefined,
        scope_org_id: undefined,
        scope_ws_id: undefined,
      });
    },
  },
}));

export const useIsUserRefreshed = () =>
  useAuthStore((state) => state.refreshed);

export const useCurrentUserId = () =>
  useAuthStore((state) => state.signed_in_user_id);

export const useUserData = () =>
  useAuthStore(
    useShallow((state) => ({
      signed_in_full_name: state.signed_in_full_name,
      signed_in_username: state.signed_in_username,
      signed_in_avatar_url: state.signed_in_avatar_url,
      signed_in_user_id: state.signed_in_user_id,
      signed_in_email: state.signed_in_email,

      api_key: state.api_key,
      hashed_email: state.hashed_email,

      roles: state.roles,
    })),
  );

export const useAuthActions = () => useAuthStore((state) => state.actions);

export const useIsScopedTo = (
  orgId: string | undefined,
  wsId: string | undefined,
) =>
  useAuthStore(
    (state) =>
      !orgId ||
      !wsId ||
      (state.scope_org_id === orgId && state.scope_ws_id === wsId),
  );

export const useIsFWDOrAdmin = () =>
  useAuthStore(
    (state) =>
      state.roles?.some((r) =>
        ["taktile_d_admin", "fwd_d", "oncall_write"].includes(r.name),
      ) ?? false,
  );

export const useIsEngineerOrAdmin = () =>
  useAuthStore(
    (state) =>
      state.roles?.some((r) =>
        [
          "taktile_d_admin",
          "taktile_admin",
          "oncall_group",
          "oncall_write",
        ].includes(r.name),
      ) ?? false,
  );

export const useHasFwdRoleForOrg = (orgId: string | undefined) =>
  useAuthStore(
    (state) =>
      !!orgId &&
      (state.roles?.some(
        (r) => r.name === "fwd_d" && r.arguments?.org_id === orgId,
      ) ??
        false),
  );

export const useIsTaktileAdmin = () =>
  useAuthStore(
    (state) =>
      state.roles?.some(
        // Oncall_write inherits from taktile_d_admin
        (r) => r.name === "taktile_d_admin" || r.name === "oncall_write",
      ) ?? false,
  );

export const useHasRole = (roleName: string) =>
  useAuthStore(
    (state) => state.roles?.some((r) => r.name === roleName) ?? false,
  );
